import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';

export default function WorkerJobs() {
  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ category_id: '', urgency: '', q: '' });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 10, status: 'active', ...filters });
    api.get(`/requirements?${params}`).then((r) => {
      setJobs(r.data.data || []);
      setPagination(r.data.pagination || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page, filters]);

  return (
    <WorkerLayout>
      <div className="space-y-5 pb-20 sm:pb-6">
        <h1 className="text-xl font-bold text-gray-900">Available Jobs</h1>

        {/* Search & Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-3">
          <input
            type="search"
            placeholder="Search jobs..."
            value={filters.q}
            onChange={(e) => { setFilters((p) => ({ ...p, q: e.target.value })); setPage(1); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
          />
          <div className="flex gap-2">
            {['critical','high','medium'].map((u) => (
              <button key={u} type="button"
                onClick={() => { setFilters((p) => ({ ...p, urgency: p.urgency === u ? '' : u })); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${filters.urgency === u ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>
                {u === 'critical' ? '🔴 Urgent' : u === 'high' ? '🟡 High' : '🟢 Normal'}
              </button>
            ))}
          </div>
        </div>

        {/* Job List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <span className="text-3xl">🔍</span>
            <p className="text-gray-500 mt-2">No jobs found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Link key={job.id} to={`/worker/jobs/${job.id}`}
                className="block bg-white rounded-xl border border-gray-200 p-4 hover:border-brand-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{job.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{job.company_name}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {job.district && <span className="text-xs text-gray-500">📍 {job.district}, {job.state}</span>}
                      {job.wage_min && <span className="text-xs text-gray-500">💰 ₹{job.wage_min}{job.wage_max ? `–${job.wage_max}` : ''}/ {job.wage_type}</span>}
                      {job.number_of_workers > 1 && <span className="text-xs text-gray-500">👥 {job.number_of_workers} workers</span>}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {job.accommodation && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">🏠 Stay</span>}
                      {job.food_provided  && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">🍽️ Food</span>}
                      {job.travel_provided && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">🚌 Travel</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Badge label={job.urgency} variant={job.urgency} />
                    {job.application_deadline && (
                      <p className="text-xs text-gray-400 mt-1 text-right">
                        Due: {new Date(job.application_deadline).toLocaleDateString('en-IN')}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => setPage(p => p - 1)}>← Prev</Button>}
            <span className="text-sm text-gray-500 py-1.5">Page {pagination.page} of {pagination.totalPages}</span>
            {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => setPage(p => p + 1)}>Next →</Button>}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
}
